/**
 * FASE 2 del plano fuente-única de pago/cobro (docs/plan-fuente-unica-pago-cobro.md §1).
 *
 * `resolverSaldo` — la CAPA DE VERDAD: una sola función que resuelve el saldo de un documento
 * fiscal leyendo ÚNICAMENTE el libro de hechos `aplicaciones_saldo` (tabla creada en Fase 1).
 * Simétrica para los dos ejes: cobro (emitidas) y pago (recibidas / externas / boletas).
 *
 * ESTADO REAL (2026-09-02): YA CABLEADO EN PRODUCCIÓN. `resolverSaldo` es la capa de verdad que
 *    consumen (directa o vía wrapper) ~12 lectores: `getCobroReal` (getCobroFactura.js) es ahora un
 *    WRAPPER puro de `resolverSaldo` (cutover R2.3), `ledgerCliente` (R2.1), FlujoCajaV2 "por cobrar"
 *    (R2.2), `ledgerProveedor`/`saldosPorFacturaProveedor` cuando reciben `ctxLibro` (R3.2 — SII/BHE
 *    del libro, EXT en FIFO a propósito), `cuotasDisponiblesDesdeLibro` en el selector de cuotas del
 *    modal (R3.3), `cargarCobroVentas`, `useDocumentosCosto`, `ncAnulaError`, `EstadoCuenta`. El libro
 *    `aplicaciones_saldo` está POBLADO (backfill Fase 3 + dual-write vivo). Mapa completo de lectores,
 *    frentes pendientes (F0–F7) y qué falta cablear: `docs/plan-cierre-fuente-unica.md`; ficha de
 *    dominio operativa: `docs/dominios/fuente-unica-pago-cobro.md`.
 *
 * Rol actual: el libro es la fuente única del eje COBRO (vía getCobroReal) y del saldo POR FACTURA
 *    de SII/BHE en el eje PAGO bajo `ctxLibro`; MBF sigue siendo el hecho autoritativo del pago
 *    bancario hasta el cutover P2.81/F4-F6. Un documento sin eventos en el libro devuelve
 *    { saldada:false, saldo:monto_total, ... } — correcto (no hay nada que reste), no un bug.
 *    Los tests de esta unidad corren sobre eventos SINTÉTICOS, no contra la DB.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * SUPERSET de la precedencia actual de `getCobroReal` (MBF → FK-directa → factoring → cuotas).
 * `getCobroReal` evalúa vías EN ORDEN y devuelve la PRIMERA que da cobrada. `resolverSaldo` no
 * necesita precedencia: SUMA todas las aplicaciones del documento en una sola Σ, cualquiera sea su
 * vía. El mapeo, para la verificación de paridad (dual-run) de Fase 3:
 *
 *   getCobroReal Camino 1 (MBF: Σ mbf.monto ≥ total)      →  eventos `via='banco'` (uno por fila MBF,
 *                                                            monto_documento = mbf.monto).
 *   getCobroReal Camino 2 (factoring liquidada)           →  UN evento `via='factoring'`,
 *                                                            monto_documento = monto_total.
 *   getCobroReal Camino 3 (cuotas todas no-pendientes)    →  un evento por cuota saldada
 *                                                            (`via='banco'`, cuota_id seteado).
 *   getCobroReal "Camino 4" (FK-directa) — YA RETIRADO    →  no se representa (igual que hoy).
 *   [nuevo respecto a getCobroReal] pre-corte recaudado   →  evento `via='corte_historico'`.
 *   [nuevo] notas de crédito                               →  evento `via='nota_credito'`
 *                                                            (monto_documento positivo — resta del
 *                                                            saldo sin plata; ver abajo).
 *   [nuevo] pagos a cuenta / anticipos sin cuota          →  evento con `cuota_id = NULL`.
 *
 * Diferencia de `fecha`: `getCobroReal` para cuotas devuelve la fecha de la ÚLTIMA cuota; aquí
 * `fecha_saldado` es la fecha del evento que hace cruzar el 100% acumulando por fecha asc — que es
 * la misma última cuota cuando las cuotas suman el total. Para MBF, ambos usan la fecha del
 * movimiento que completa el total. Equivalentes por construcción.
 *
 * VENTAJA ESTRUCTURAL sobre el sistema viejo: como suma N eventos por documento (y por cuota),
 * resuelve el caso MMS (una cuota pagada por 2 movimientos bancarios) que el vínculo escalar
 * `plan_pagos_facturas.movimiento_bancario_id` no podía representar. Ver docs/fase0-reconciliacion-
 * reporte.md B.5 y docs/puente-cuota-movimiento-diseno.md.
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 *
 * TOLERANCIA — espejo EXACTO del CONSTRAINT TRIGGER de Fase 1 (`aplicaciones_saldo_no_sobre_saldar`):
 *   · misma moneda (todos los eventos en la moneda del documento): FIJA de 1 unidad (redondeo),
 *     igual que el candado I2. Sin margen relativo.
 *   · cross-currency (algún evento en otra moneda, ej. factura USD/UF pagada en CLP): 5% del total,
 *     el mismo % que ya usan las reglas 10.67 / 10.44 por drift de TC entre fecha de factura y de
 *     pago.
 * La suma se hace SIEMPRE en la moneda del documento, usando `monto_documento` (ya convertido y
 * congelado por evento en Fase 1) — PROHIBIDO raw-sum de `monto_origen` (regla 10.185).
 */

export const TOLERANCIA_MISMA_MONEDA = 1; // unidad de la moneda del documento (= candado I2)
export const TOLERANCIA_CROSS_CURRENCY_PCT = 0.05; // 5% por drift de TC (10.67 / 10.44)

// Núcleo puro compartido (10.1): saldo = total − Σ aplicado, y "saldada" con tolerancia. Lo usan
// `resolverSaldo` (eje pago/cobro, libro aplicaciones_saldo) y `resolverAtribucionFactura`
// (eje atribución de ingreso, puente facturas_sii_vinculaciones) — una sola fórmula de saldo.
export const computarSaldo = (total, aplicado, tolerancia = TOLERANCIA_MISMA_MONEDA) => {
  const saldo = Number(total || 0) - Number(aplicado || 0);
  return { saldo, saldada: saldo <= tolerancia };
};

const claveDoc = (tipo, id) => `${tipo}:${id}`;

/**
 * FASE 5 (lector) — saldo pendiente POR CUOTA derivado del libro `aplicaciones_saldo`, para el
 * selector de cuotas de la pantalla de vinculación. Una cuota está DISPONIBLE si lo que le queda por
 * saldar (monto_cuota − Σ monto_documento de los eventos con ese cuota_id) supera la tolerancia — NO
 * si su flag `estado` escalar sigue en 'pendiente'. Resuelve el caso MMS: una cuota con un evento
 * parcial ($1.000.000 de $1.415.326) aparece disponible por su saldo real ($415.326), no "pagada".
 *
 * Devuelve las cuotas disponibles con su `monto_cuota` REEMPLAZADO por el saldo pendiente (para que
 * el tilde, el cuadre y el dual-write apliquen el remanente), y `_montoOriginal` / `_aplicado` /
 * `_saldoPendiente` para el display. Las cuotas saldadas por el libro quedan fuera.
 *
 * @param {Array} cuotas   filas del plan (plan_pagos_facturas / plan_cobros_facturas).
 * @param {Array} eventos  filas de aplicaciones_saldo del documento (con cuota_id, monto_documento).
 * @param {{tolerancia?:number}} opts
 */
export const cuotasDisponiblesDesdeLibro = (
  cuotas = [],
  eventos = [],
  { tolerancia = TOLERANCIA_MISMA_MONEDA } = {}
) => {
  const aplicadoPorCuota = new Map();
  for (const e of eventos || []) {
    if (e?.cuota_id == null) continue;
    aplicadoPorCuota.set(e.cuota_id, (aplicadoPorCuota.get(e.cuota_id) || 0) + Number(e.monto_documento || 0));
  }
  return (cuotas || [])
    .map((c) => {
      const original = Math.abs(Number(c.monto_cuota || 0));
      const aplicado = aplicadoPorCuota.get(c.id) || 0;
      const saldo = original - aplicado;
      return { ...c, monto_cuota: saldo, _montoOriginal: original, _aplicado: aplicado, _saldoPendiente: saldo };
    })
    .filter((c) => c._saldoPendiente > tolerancia);
};

/**
 * Moneda del documento. Las recibidas y las boletas de honorarios son SIEMPRE domésticas (CLP) —
 * no tienen columna `moneda` (idéntico criterio al del trigger de Fase 1). Las emitidas y externas
 * traen su moneda (una emitida DTE 110 puede ser USD; una externa suele ser USD).
 */
export const monedaDocumento = (documento) => {
  if (!documento) return "CLP";
  if (documento.tipo === "sii_recibida" || documento.tipo === "boleta_honorario") return "CLP";
  return documento.moneda || "CLP";
};

/**
 * Indexa las filas del libro por documento, para que `resolverSaldo` sea O(k) por documento.
 * @param {Array} aplicaciones filas de `aplicaciones_saldo` (documento_tipo, documento_id, via,
 *   monto_documento, moneda_origen, fecha_evento, monto, cuota_id).
 * @returns {{ porDocumento: Map<string, Array> }}
 */
export const buildContextoLibro = (aplicaciones = []) => {
  const porDocumento = new Map();
  for (const a of aplicaciones || []) {
    if (a?.documento_tipo == null || a?.documento_id == null) continue;
    const k = claveDoc(a.documento_tipo, a.documento_id);
    if (!porDocumento.has(k)) porDocumento.set(k, []);
    porDocumento.get(k).push(a);
  }
  return { porDocumento };
};

/**
 * Resuelve el saldo de un documento leyendo SOLO el libro (plano §1).
 * @param {{ tipo:string, id:number, monto_total:number, moneda?:string }} documento
 *   tipo ∈ sii_emitida | sii_recibida | factura_externa | boleta_honorario.
 * @param {{ porDocumento: Map }} ctxLibro  resultado de buildContextoLibro.
 * @returns {{ saldada:boolean, saldo:number, fecha_saldado:string|null,
 *             via:('banco'|'factoring'|'nota_credito'|'anticipo'|'corte_historico'|null),
 *             parcial:boolean }}
 */
export const resolverSaldo = (documento, ctxLibro) => {
  if (!documento || documento.id == null || documento.tipo == null) {
    return { saldada: false, saldo: 0, fecha_saldado: null, via: null, parcial: false };
  }

  const total = Number(documento.monto_total || 0);
  const eventos = ctxLibro?.porDocumento?.get(claveDoc(documento.tipo, documento.id)) || [];

  // Sin aplicaciones: saldo = total, NO saldada, NO parcial. (Es el estado de TODO documento hoy,
  // porque el libro está vacío hasta el backfill de Fase 3 — no es un bug, ver cabecera.)
  if (eventos.length === 0) {
    return { saldada: false, saldo: total, fecha_saldado: null, via: null, parcial: false };
  }

  const monedaDoc = monedaDocumento(documento);
  const hayCross = eventos.some((e) => (e.moneda_origen || monedaDoc) !== monedaDoc);
  const tol = hayCross ? total * TOLERANCIA_CROSS_CURRENCY_PCT : TOLERANCIA_MISMA_MONEDA;

  // Orden por fecha del evento asc; desempate estable por orden de llegada (índice original).
  const orden = eventos
    .map((e, i) => ({ e, i }))
    .sort((a, b) => {
      const fa = String(a.e.fecha_evento ?? "9999-12-31");
      const fb = String(b.e.fecha_evento ?? "9999-12-31");
      return fa === fb ? a.i - b.i : fa.localeCompare(fb);
    })
    .map((x) => x.e);

  // Σ en la moneda del documento (monto_documento ya convertido/congelado — 10.185). La NC ya viene
  // con monto_documento POSITIVO (magnitud que reduce saldo, como se resolvió en Fase 1); el signo
  // contable vive en `monto`, que NO se usa acá.
  let acum = 0;
  let fechaSaldado = null;
  let viaCierre = null;
  for (const e of orden) {
    acum += Number(e.monto_documento || 0);
    if (fechaSaldado === null && acum >= total - tol) {
      fechaSaldado = e.fecha_evento ?? null;
      viaCierre = e.via ?? null;
    }
  }

  const { saldo, saldada } = computarSaldo(total, acum, tol);

  return {
    saldada,
    saldo,
    fecha_saldado: saldada ? fechaSaldado : null,
    via: saldada ? viaCierre : null,
    parcial: eventos.length > 0 && !saldada, // hay aplicaciones pero el saldo no cerró
  };
};
